import { Box, Button, Dialog, DialogActions, DialogContent, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import CustomDialogTitle from 'components/modal/CustomDialogTitle';
import GrowDialogTransition from 'components/modal/GrowDialogTransition';
import { useFormContext, useWatch } from 'react-hook-form';
import WriteWithAIForm from './WriteWithAIForm';
import { WriteWithAIOnSelectOptions, WriteWithAITabName } from '../../hooks/useWriteWithAI';
import { WriteWithAIFormValues } from '../../hooks/useWriteWithAIForm';
// import WriteWithAIPromptHistory from './WriteWithAIPromptHistory';
import { useMemo, useRef } from 'react';
import { ReactComponent as SparkIcon } from 'assets/icons/auto-generated.svg';
import { AnswerContainer } from '@buildwise/pages/buildwiseAdmin/BuildWiseGPT/PromptHistory';
import RenderLLMMarkdown from '@buildwise/pages/buildwiseAdmin/BuildWiseGPT/RenderLLMMarkdown';
import WriteWithAISkeleton from './WriteWithAISkeleton';
import RenderAttachments from './RenderAttachments';
import { AttachmentType } from '@buildwise/pages/knowledge/hooks/useArticlesQuery';
import { isEmpty } from 'lodash';

type WriteWithAIModalProps = {
  open: boolean;
  tab: WriteWithAITabName;
  closeModal: () => void;
  onGenerate: () => void;
  onSelect: (options: WriteWithAIOnSelectOptions) => void;
  onRetry: (options: { text: string }) => void;
};

function WriteWithAIModal({
  open,
  tab,
  closeModal,
  onGenerate,
  onSelect,
  onRetry,
}: WriteWithAIModalProps) {
  const {
    control,
    formState: { isSubmitting, isDirty, isValid },
  } = useFormContext<WriteWithAIFormValues>();
  const { text = [] } = useWatch({
    control,
  });
  const lastPrompt = useMemo(() => text[text.length - 1], [text]);
  const containerRef = useRef<HTMLDivElement | null>(null);

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="md"
      aria-labelledby="dialog-title"
      aria-describedby="dialog-description"
      TransitionComponent={GrowDialogTransition}
      sx={{ '& .MuiPaper-root': { maxHeight: '80%', minHeight: '60%' } }}
    >
      <CustomDialogTitle
        onClose={closeModal}
        id="dialog-title"
        sx={{ textAlign: 'center', borderBottom: 'none', pb: 1 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <SparkIcon width={24} height={24} />
          <Title>Write with AI</Title>
        </Box>
        <Note>Polish your information into engaging posts with our AI writing assistant.</Note>
      </CustomDialogTitle>
      <DialogContent id="dialog-description" ref={containerRef}>
        {/* <WriteWithAIPromptHistory
              onSelect={onSelect}
              onRetry={(options) => {
                onRetry(options);
                setTimeout(() => {
                  if (containerRef.current) {
                    containerRef.current.scrollIntoView({
                      behavior: 'smooth',
                      block: 'end',
                    });
                  }
                }, 100);
              }}
            /> */}
        {!lastPrompt.genAIResponse && !lastPrompt.loading && (
          <WriteWithAIForm tab={tab} onGenerate={onGenerate} />
        )}
        {(lastPrompt?.loading || lastPrompt?.genAIResponse) && (
          <AnswerContainer
            width="100% !important"
            padding="16px !important"
            sx={{ borderRadius: '8px', mr: 0 }}
          >
            {lastPrompt?.loading ? (
              <WriteWithAISkeleton />
            ) : (
              <>
                <RenderLLMMarkdown llmResponse={lastPrompt?.genAIResponse || ''} />
              </>
            )}
          </AnswerContainer>
        )}
        {!isEmpty(lastPrompt.embeddedAttachments) && (
          <RenderAttachments attachments={lastPrompt.embeddedAttachments as AttachmentType[]} />
        )}
        {/* <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: 1 }}>
                <Tabs
                  value={tab}
                  onChange={(_: unknown, newValue: TabNameType) => {
                    setTab(newValue);
                    reset();
                  }}
                  TabIndicatorProps={{
                    style: {
                      display: 'none',
                    },
                  }}
                  sx={{
                    border: 'none',
                    '& .MuiTab-root': {
                      margin: 0,
                      border: 'none',
                      backgroundColor: '#F5F6F7',
                      color: '#35374F',
                      textTransform: 'none',
                      padding: '8px, 12px, 8px, 12px',
                      fontWeight: 700,
                      '&:hover': {
                        backgroundColor: '#b3b3b3',
                      },
                      '&.Mui-selected': {
                        backgroundColor: '#35374F',
                        color: '#FFFFFF',
                      },
                    },
                  }}
                >
                  <Tab label="Add Text" value="content" />
                  <Tab label="Upload File" value="file" />
                  <Tab label="Add URL" value="url" />
                </Tabs>
              </Box> */}
      </DialogContent>
      <DialogActions>
        {!lastPrompt?.loading && !lastPrompt?.genAIResponse && (
          <Button
            disabled={isSubmitting || !isDirty || !isValid}
            onClick={onGenerate}
            variant="contained"
          >
            Generate
          </Button>
        )}
        {lastPrompt?.genAIResponse && !lastPrompt?.isStreaming && (
          <Button
            disabled={isSubmitting || !isDirty || !isValid}
            onClick={() =>
              onSelect({
                ...lastPrompt?.articleMetaData,
                embeddedAttachments: (lastPrompt.embeddedAttachments || []) as AttachmentType[],
              })
            }
            variant="contained"
          >
            Next
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default WriteWithAIModal;

const Note = styled(Typography, { label: 'Note' })(({ theme }) => ({
  fontSize: 14,
  color: theme.palette.custom.tuna,
  textAlign: 'left',
  marginTop: 4,
  marginBottom: 8,
}));

const Title = styled(Typography, { label: 'Title' })(({ theme }) => ({
  fontSize: 20,
  fontWeight: 700,
  color: theme.palette.custom.link,
  marginLeft: 8,
}));
